import { FC, useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid/Grid';
import { SelectChangeEvent } from '@mui/material/Select';
import Link from '@mui/material/Link';
import { Text } from '@concepta/react-material-ui';
import useLocalStorageState from 'use-local-storage-state';
import ReactGA from 'react-ga4';
import { FormContainer } from 'app/hooks/useGlobalForm';
import Header from 'app/components/Header';
import Footer from 'app/components/Footer';
import ErrorText from 'app/components/ErrorText';
import StrollersCostSummary from './Components/StrollersCostSummary';
import EditOrderCostSummary from './Components/EditOrderCostSummary';
import useOrderCheckout, {
  CustomerForm,
  AgentForm,
} from 'app/hooks/useOrderCheckout';
import useOrderUpdate from 'app/hooks/useOrderUpdate';
import {
  OrderQuoteOutputInterface,
  OrderCheckoutOutputInterface,
  OrderEditLoadInterface,
} from 'ks-common';
import cloneDeep from 'lodash/cloneDeep';
import useOrderQuote from 'app/hooks/useOrderQuote';
import useTapConversion from 'app/hooks/useTapConversion';
import Customer from './Components/CustomerForm';
import Agent from './Components/AgentForm';
import Recaptcha from './Components/Recaptcha';
import { isMobile } from 'utils/isMobile';
import isToday from 'date-fns/isToday';
import useDebounce from 'app/hooks/useDebounce';
import useCheckoutLocalStorage, {
  CheckoutForm,
} from 'app/hooks/useCheckoutLocalStorage';
import useScrollTop from 'app/hooks/useScrollTop';
import Braintree from './Components/Braintree';
import RentalAgreementCheck from './Components/RentalAgreementCheck';
import { agentValidate, customerValidate, FormErrors } from './validators';
import { gaCartFormatter } from 'utils/GAnalytics/formatters';
import { DateObject } from 'react-multi-date-picker';
import { OnChangeTextField } from './Components/CheckoutTextField';
import { toast } from 'react-toastify';
import generateUuid from 'utils/generateUuid';
import { ContactMethod } from 'app/components/ContactMethodToggle';
import { ParsedCountry } from 'react-international-phone';
import useBraintreePayment from 'app/hooks/useBraintreePayment';

const SHOW_RECAPTCHA = process.env.REACT_APP_SHOW_RECAPTCHA;
const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_KEY;

const CheckoutMobile: FC = () => {
  useScrollTop(true);
  const [tapfiliateVid] = useLocalStorageState<string>('tapVid');

  const navigate = useNavigate();
  const {
    products,
    orderQuoteId,
    isEdit,
    originalOrder,
    customerInfo,
    setCustomerInfo,
    setOriginalOrder,
    setOrderQuoteId,
    validations,
  } = FormContainer.useContainer();
  const { checkoutForm, updateCheckoutForm } = useCheckoutLocalStorage();
  const [gaSent, setGaSent] = useState(false);

  useEffect(() => {
    if (!validations.cartLocationsIncomplete && !validations.notesTooLong)
      return;
    const redirectTo =
      isEdit && originalOrder?.OrderId
        ? `/edit-order/${originalOrder.OrderId}`
        : '/review';
    navigate(redirectTo, { replace: true });
  }, [
    validations.cartLocationsIncomplete,
    validations.notesTooLong,
    isEdit,
    originalOrder?.OrderId,
  ]);

  const {
    data: quoteData,
    execute: quoteExecute,
    isPending: quoteIsPending,
  } = useOrderQuote(products, originalOrder?.OrderId);

  const valueDifference = quoteData?.PendingAmountValue;
  // One value drives both the wallet sheet and the submitted charge.
  const payableAmount = isEdit ? valueDifference : quoteData?.TotalValue;
  const {
    status: braintreeStatus,
    requestPaymentNonce,
    clearSelectedPaymentMethod,
  } = useBraintreePayment({ amount: payableAmount, isEdit });

  useEffect(() => {
    if (products) {
      quoteExecute();
    }
  }, [products]);

  useEffect(() => {
    if (quoteData && !gaSent && !isEdit) {
      const gaData = gaCartFormatter(quoteData);
      ReactGA.event('begin_checkout', gaData);
      setGaSent(true);
    }
  }, [quoteData]);

  const [searchParams] = useSearchParams();
  const refQuery = searchParams.get('ref');

  const [recaptchaToken, setRecaptchaToken] = useState<string>();
  const [recaptchaError, setRecaptchaError] = useState<string>('');
  const [customerErrors, setCustomerErrors] = useState<
    FormErrors<CustomerForm>
  >({});
  const [customerForm, setCustomerForm] = useState<Partial<CustomerForm>>({
    country: 'US',
  });
  const [agentErrors, setAgentErrors] = useState<FormErrors<AgentForm>>({});
  const [agentForm, setAgentForm] = useState<Partial<AgentForm>>({});

  useEffect(() => {
    if (!isEdit && checkoutForm?.customerForm) {
      setCustomerForm(checkoutForm.customerForm);
    }

    if (
      !isEdit &&
      checkoutForm?.agentForm &&
      Object.keys(checkoutForm.agentForm).length
    ) {
      setIsAgent(true);
      setAgentForm(checkoutForm.agentForm);
    }

    if (isEdit && customerInfo) {
      setCustomerForm({
        ...customerInfo,
        confirmPrimaryCellPhone: customerInfo.primaryCellPhone,
      });
    }
  }, []);

  const debouncedCustomerForm = useDebounce<Partial<CustomerForm>>(
    customerForm,
    300,
  );
  const debouncedAgentForm = useDebounce<Partial<AgentForm>>(agentForm, 300);

  useEffect(() => {
    const forms: CheckoutForm = {
      customerForm,
      agentForm,
    };
    isEdit && setCustomerInfo(customerForm);
    !isEdit && updateCheckoutForm(forms);
  }, [debouncedCustomerForm, debouncedAgentForm]);

  const [isAgent, setIsAgent] = useState<boolean>(false);

  const handleTravelAgent = () => {
    setIsAgent(prev => !prev);
  };

  const onChange: OnChangeTextField = ({ name, value }) => {
    setCustomerForm(prev => ({ ...prev, [name]: value }));
  };

  const onSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setCustomerForm({ ...customerForm, [name]: value });
  };

  const agentOnChange: OnChangeTextField = ({ name, value }) => {
    setAgentForm({ ...agentForm, [name]: value });
  };

  const onPhoneChange = (
    name: string,
    phone: string,
    country: ParsedCountry,
  ) => {
    const updates: Partial<CustomerForm> = { [name]: phone };

    if (name === 'primaryCellPhone') {
      updates.countryCodeCellPhone = country.dialCode;
    } else if (name === 'secondaryCellPhone') {
      updates.countryCodeSecondCellPhone = country.dialCode;
    }

    setCustomerForm(prev => ({ ...prev, ...updates }));
  };

  const onContactMethodChange = (method: ContactMethod) => {
    setCustomerForm(prev => ({ ...prev, preferredContactMethod: method }));
  };

  useEffect(() => {
    if (Object.keys(customerErrors).length > 0) {
      customerValidate({
        customerForm,
        recaptchaToken,
        setRecaptchaError,
        setCustomerErrors,
      });
    }
  }, [customerForm]);

  useEffect(() => {
    if (Object.keys(agentErrors).length > 0) {
      agentValidate({
        agentForm,
        isAgent,
        setAgentErrors,
      });
    }
  }, [agentForm]);

  const validateForms = () => {
    const formValidation = customerValidate({
      customerForm,
      recaptchaToken,
      setRecaptchaError,
      setCustomerErrors,
    });
    const agentValidation = agentValidate({
      agentForm,
      isAgent,
      setAgentErrors,
    });

    return formValidation && agentValidation;
  };

  const hasErrors = useMemo(() => {
    if (Object.keys(customerErrors).length > 0) return true;

    return isAgent && Object.keys(agentErrors).length > 0;
  }, [customerErrors, agentErrors]);

  const [nonce, setNonce] = useState<string>('');
  const [braintreeIsPending, setBraintreeIsPending] = useState<boolean>(false);

  const {
    data: checkoutData,
    execute: executeCheckout,
    isPending: checktoutIsPending,
    error,
  } = isEdit
    ? useOrderUpdate({
        orderId: originalOrder?.OrderId,
        products,
        customer: customerForm as CustomerForm,
        paymentInfo: {
          Nounce: nonce,
          Value: payableAmount,
        },
        orderQuoteId: orderQuoteId,
        onSuccess: (order: OrderCheckoutOutputInterface) => {
          if (originalOrder && order) {
            const newOrderData: OrderEditLoadInterface =
              cloneDeep(originalOrder);
            newOrderData.OrderQuoteOutput = cloneDeep(
              order.OrderQuoteCalculated,
            );
            newOrderData.Customer = {
              ...newOrderData.Customer,
              ...order.Customer,
            };

            setOriginalOrder(newOrderData);
            setOrderQuoteId(generateUuid(true));
            navigate('/edit-order/thank-you', {
              state: order,
            });
          }
        },
      })
    : useOrderCheckout({
        products,
        customer: customerForm as CustomerForm,
        agent: agentForm as AgentForm,
        paymentInfo: {
          Nounce: nonce,
          Value: payableAmount,
        },
        orderQuoteId: orderQuoteId,
      });

  const {
    sendComission,
    isPending: conversionIsPending,
    status,
  } = useTapConversion({
    orderId: checkoutData?.OrderId,
    orderAmount: checkoutData?.OrderQuoteCalculated?.SubTotalValue,
    billingName: `${customerForm?.firstName} ${customerForm?.lastName}`,
  });

  useEffect(() => {
    if (checkoutData && tapfiliateVid) {
      sendComission();
    }
    if (checkoutData && !tapfiliateVid && !isEdit) {
      setOrderQuoteId(generateUuid());
      navigate('/thank-you', {
        state: checkoutData,
      });
    }
  }, [checkoutData]);

  useEffect(() => {
    if (status === 'success') {
      setOrderQuoteId(generateUuid());
      navigate('/thank-you', {
        state: checkoutData,
      });
    }
  }, [status]);

  useEffect(() => {
    if (nonce) {
      executeCheckout();
    }
  }, [nonce]);

  const changeAccept = (checked: boolean) => {
    setCustomerForm({ ...customerForm, rentalAgreement: checked });
  };

  const handleSubmit = async () => {
    if (!validateForms()) return;

    setBraintreeIsPending(true);
    try {
      setNonce(await requestPaymentNonce());
    } catch {
      toast.error('Please select a payment method');
    } finally {
      setBraintreeIsPending(false);
    }
  };

  const onChangeCaptcha = (token: string | null) => {
    if (token) {
      setRecaptchaToken(token);

      if (recaptchaError) {
        validateForms();
      }
    }
  };

  const expiredCaptcha = () => {
    setRecaptchaToken('');
  };

  const deliveryDateIsToday = products.some(
    prod =>
      !!prod.deliveryDate &&
      isToday(
        new DateObject({
          date: prod.deliveryDate,
          format: 'YYYY-MM-DD',
        }).toDate(),
      ),
  );

  const goToReview = () => {
    navigate('/review');
  };

  const isLoading =
    braintreeIsPending || checktoutIsPending || conversionIsPending;

  useEffect(() => {
    if (error) {
      clearSelectedPaymentMethod();
    }
  }, [error, clearSelectedPaymentMethod]);

  return (
    <>
      <Box minHeight="100vh" width="100vw">
        <Header isEdit={isEdit} />
        <Container maxWidth="xl">
          <Grid item container spacing={4} p={2}>
            <Grid item xs={12} md={7}>
              <Text
                fontSize={isMobile ? 24 : 36}
                fontWeight={700}
                color="primary.darker"
                align={isMobile ? 'center' : 'left'}
              >
                Secure Checkout
              </Text>
              <Text
                fontSize={isMobile ? 20 : 25}
                fontWeight={400}
                mt={3}
                display={isMobile ? 'block' : 'inline'}
                color={isMobile ? 'grey.900' : 'primary.darker'}
                textTransform={isMobile ? 'uppercase' : 'none'}
              >
                Billing Address Information{isMobile ? ':' : ''}
              </Text>
              <Text
                fontSize={isMobile ? 16 : 25}
                fontWeight={400}
                display={isMobile ? 'block' : 'inline'}
                color="primary.darker"
                textTransform={isMobile ? 'uppercase' : 'none'}
              >
                {isMobile ? '' : ' '}(Not Your Vacation Address)
              </Text>

              <Box mt={3}>
                <Customer
                  form={customerForm}
                  errors={customerErrors}
                  onChange={onChange}
                  onSelectChange={onSelectChange}
                  onPhoneChange={onPhoneChange}
                  onContactMethodChange={onContactMethodChange}
                  isEdit={isEdit}
                />
              </Box>

              <Braintree status={braintreeStatus} />

              {!refQuery && !tapfiliateVid && !isEdit && (
                <Agent
                  agentForm={agentForm}
                  agentErrors={agentErrors}
                  agentOnChange={agentOnChange}
                  handleTravelAgent={handleTravelAgent}
                  isAgent={isAgent}
                />
              )}
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: '24px',
                }}
              >
                {!deliveryDateIsToday &&
                  (isEdit ? (
                    <EditOrderCostSummary
                      total={valueDifference}
                      isPending={quoteIsPending}
                    />
                  ) : (
                    <StrollersCostSummary
                      quoteData={quoteData as OrderQuoteOutputInterface}
                      isPending={quoteIsPending}
                    />
                  ))}

                <RentalAgreementCheck
                  changeAccept={changeAccept}
                  checked={customerForm?.rentalAgreement}
                  customerErrors={customerErrors}
                />

                {!isMobile && !!SHOW_RECAPTCHA && RECAPTCHA_KEY && (
                  <Box mt={3}>
                    <Recaptcha
                      onChange={onChangeCaptcha}
                      expiredCallback={expiredCaptcha}
                    />
                  </Box>
                )}

                {recaptchaError && <ErrorText>{recaptchaError}</ErrorText>}

                {hasErrors && (
                  <ErrorText>
                    Please check all required fields before completing your
                    order.
                  </ErrorText>
                )}

                {deliveryDateIsToday && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems={isMobile ? 'center' : 'flex-start'}
                  >
                    <ErrorText sx={{ my: 2 }}>
                      Please review and enter a valid delivery date.
                    </ErrorText>
                    <Link
                      sx={{ cursor: 'pointer' }}
                      onClick={goToReview}
                      fontWeight={500}
                    >
                      RETURN TO REVIEW ORDER SCREEN
                    </Link>
                  </Box>
                )}

                {isLoading && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mt={4}
                  >
                    <CircularProgress />
                  </Box>
                )}

                {braintreeStatus !== 'error' && !deliveryDateIsToday && (
                  <Text
                    fontSize={isMobile ? 12 : 14}
                    fontWeight={300}
                    align={isMobile ? 'center' : 'left'}
                    mt={4}
                  >
                    Your payment method will be securely saved with our payment
                    provider so you can easily pay for changes to this
                    reservation.
                  </Text>
                )}

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleSubmit}
                  fullWidth
                  disabled={
                    isLoading ||
                    deliveryDateIsToday ||
                    braintreeStatus !== 'ready'
                  }
                >
                  {isEdit ? 'PAY BALANCE' : 'Send Order'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
        {!isMobile && <Footer />}
      </Box>
    </>
  );
};

export default CheckoutMobile;
